import { useMemo, useState } from "react";
import { Avatar, Empty, Input, Segmented, Select } from "antd";
import dayjs from "dayjs";
import { Search } from "lucide-react";
import {
    HISTORY_CATEGORY, SYSTEM_AGENT, UNASSIGNED, useCallCenter, type Customer, type HistoryCategory, type HistoryEntry,
} from "../../CustomerDemo/callCenterStore";
import { normalize } from "../../Social/socialStore";

// Viết tắt tên người dùng để hiển thị trên avatar (vd: "truc page admin" -> "TP")
const getInitials = (name?: string) => {
    if (!name) return "HT";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "HT";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const WEEKDAY = ["Chủ nhật", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
const dayLabel = (t: number) => {
    const d = dayjs().startOf("day").diff(dayjs(t).startOf("day"), "day");
    return d === 0 ? "Hôm nay" : d === 1 ? "Hôm qua" : `${WEEKDAY[dayjs(t).day()]}, ${dayjs(t).format("DD/MM/YYYY")}`;
};

type Filter = "all" | HistoryCategory;

/** Lịch sử cập nhật hồ sơ khách: ai đổi gì, từ → thành, lý do; kèm sự kiện lịch hẹn / hoá đơn / ảnh / chặn gọi */
export default function HistoryList({ customer }: { customer: Customer }) {
    const s = useCallCenter();
    const [filter, setFilter] = useState<Filter>("all");
    const [person, setPerson] = useState<string>();
    const [q, setQ] = useState("");

    // Dòng "Tạo hồ sơ" luôn ở cuối — suy ra từ thông tin khách, không cần lưu riêng
    const items = useMemo<HistoryEntry[]>(() => [
        ...s.history.filter(h => h.customerId === customer.id),
        {
            id: `created-${customer.id}`, customerId: customer.id, label: "Tạo hồ sơ khách hàng", from: "",
            to: [customer.source && `Nguồn: ${customer.source}`, customer.referrer && `Giới thiệu: ${customer.referrer}`, `SĐT ${customer.phones[0]}`].filter(Boolean).join(" · "),
            by: customer.createdBy || (customer.agent !== UNASSIGNED ? customer.agent : SYSTEM_AGENT), at: customer.createdAt, category: "info" as const,
        },
    ].sort((a, b) => b.at - a.at), [s.history, customer]);

    const people = useMemo(() => Array.from(new Set(items.map(i => i.by))), [items]);
    const count = (c: HistoryCategory) => items.filter(i => (i.category || "info") === c).length;
    const kw = normalize(q);
    const shown = items.filter(i => (filter === "all" || (i.category || "info") === filter)
        && (!person || i.by === person)
        && (!kw || normalize(`${i.label} ${i.from} ${i.to} ${i.note || ""}`).includes(kw)));
    const groups = shown.reduce<{ day: string; items: HistoryEntry[] }[]>((out, it) => {
        const day = dayLabel(it.at);
        const last = out[out.length - 1];
        if (last && last.day === day) last.items.push(it); else out.push({ day, items: [it] });
        return out;
    }, []);

    return (
        <div className="space-y-4">
            <div className="overflow-x-auto">
                <Segmented value={filter} onChange={v => setFilter(v as Filter)} options={[
                    { value: "all", label: `Tất cả (${items.length})` },
                    ...(Object.keys(HISTORY_CATEGORY) as HistoryCategory[]).filter(c => count(c)).map(c => ({ value: c, label: `${HISTORY_CATEGORY[c].label} (${count(c)})` })),
                ]} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input allowClear prefix={<Search size={14} className="text-gray-400" />} placeholder="Tìm trường, giá trị, lý do" value={q} onChange={e => setQ(e.target.value)} />
                <Select allowClear placeholder="Lọc theo người cập nhật" value={person} onChange={setPerson} options={people.map(p => ({ value: p, label: p }))} />
            </div>

            {groups.length === 0 ? (
                <div className="py-10"><Empty description="Không có thay đổi khớp bộ lọc" /></div>
            ) : (
                <div className="relative px-4 py-2">
                    <div className="absolute left-4 top-0 bottom-0 w-[2px] bg-gray-200 dark:bg-gray-700" />
                    {groups.map(g => (
                        <div key={g.day}>
                            <div className="relative ml-6 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">{g.day}</div>
                            {g.items.map(item => {
                                const cat = HISTORY_CATEGORY[item.category || "info"];
                                const isEvent = item.from === "";
                                return (
                                    <div key={item.id} className="relative mb-4">
                                        <div className="absolute left-[1px] top-3 -translate-x-1/2 w-3 h-3 bg-white rounded-full border-4" style={{ borderColor: cat.color }} />
                                        <div className="ml-6 bg-gray-50 rounded-lg p-3 dark:bg-[#1f1f1f]">
                                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                                <Avatar size={24} style={{ background: cat.color }}>{getInitials(item.by)}</Avatar>
                                                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{item.by}</span>
                                                <span className="text-xs text-gray-400">{isEvent ? "·" : "đã cập nhật"}</span>
                                                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{item.label}</span>
                                                <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ color: cat.color, background: `${cat.color}14` }}>{cat.label}</span>
                                                <span className="text-xs text-gray-400 ml-auto">{dayjs(item.at).format("HH:mm DD/MM/YYYY")}</span>
                                            </div>
                                            {isEvent ? (
                                                <div className="text-sm text-gray-800 dark:text-gray-100 break-words">{item.to}</div>
                                            ) : (
                                                <div className="text-sm flex items-center gap-2 flex-wrap">
                                                    <span className="line-through text-gray-400 break-all">{item.from}</span>
                                                    <span className="text-gray-400">→</span>
                                                    <span className="text-gray-800 dark:text-gray-100 font-medium break-all">{item.to}</span>
                                                </div>
                                            )}
                                            {item.note && <div className="text-xs text-gray-500 mt-1.5"><span className="text-gray-400">Lý do / ghi chú:</span> {item.note}</div>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
